import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Package, ArrowLeft, MapPin, CreditCard } from 'lucide-react';
import api from '../utils/api';

const STATUS_STEPS = ['pending', 'confirmed', 'processing', 'shipped', 'delivered'];
const STATUS_COLORS = { pending: '#f59e0b', confirmed: '#3b82f6', processing: '#8b5cf6', shipped: '#06b6d4', delivered: '#10b981', cancelled: '#ef4444' };

export default function OrderDetailPage() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/orders/${id}`).then(res => { setOrder(res.data.order); setLoading(false); }).catch(() => setLoading(false));
  }, [id]);

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 100 }}><div className="spinner" /></div>;
  if (!order) return <div className="container" style={{ padding: 60 }}><h2>Order not found</h2></div>;

  const currentStep = STATUS_STEPS.indexOf(order.orderStatus);

  return (
    <div style={{ padding: '48px 0 80px' }} className="container">
      <Link to="/orders" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--primary)', fontWeight: 600, marginBottom: 28 }}>
        <ArrowLeft size={16} /> Back to Orders
      </Link>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
        <h1 style={{ fontSize: 28 }}>Order {order.orderNumber}</h1>
        <span style={{ background: STATUS_COLORS[order.orderStatus] + '22', color: STATUS_COLORS[order.orderStatus], padding: '6px 16px', borderRadius: 20, fontWeight: 700, fontSize: 14, textTransform: 'capitalize' }}>
          {order.orderStatus}
        </span>
      </div>

      {/* Progress tracker */}
      {order.orderStatus !== 'cancelled' && (
        <div style={{ background: 'var(--white)', borderRadius: 'var(--radius-lg)', padding: 28, marginBottom: 24, boxShadow: 'var(--shadow-sm)', border: '1px solid var(--gray-100)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
            <div style={{ position: 'absolute', top: '18px', left: '10%', right: '10%', height: 3, background: 'var(--gray-200)', zIndex: 0 }} />
            {STATUS_STEPS.map((s, i) => (
              <div key={s} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, zIndex: 1 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: i <= currentStep ? 'var(--primary)' : 'var(--gray-200)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '3px solid var(--white)' }}>
                  {i <= currentStep ? <span style={{ color: 'var(--gold)', fontWeight: 800, fontSize: 14 }}>✓</span> : <span style={{ color: 'var(--gray-400)', fontSize: 12 }}>{i + 1}</span>}
                </div>
                <span style={{ fontSize: 12, fontWeight: 600, color: i <= currentStep ? 'var(--primary)' : 'var(--gray-400)', textTransform: 'capitalize' }}>{s}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 24 }}>
        <div style={{ background: 'var(--white)', borderRadius: 'var(--radius-lg)', padding: 28, boxShadow: 'var(--shadow-sm)', border: '1px solid var(--gray-100)' }}>
          <h3 style={{ marginBottom: 20 }}>Order Items</h3>
          {order.items?.map(item => (
            <div key={item._id} style={{ display: 'flex', gap: 14, alignItems: 'center', padding: '12px 0', borderBottom: '1px solid var(--gray-100)' }}>
              <img src={item.thumbnail || 'https://images.unsplash.com/photo-1447933601403-0c6688de566e?w=80'} alt={item.name} style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 8 }} />
              <div style={{ flex: 1 }}>
                <p style={{ fontWeight: 600, color: 'var(--gray-800)' }}>{item.name}</p>
                <p style={{ fontSize: 13, color: 'var(--gray-500)' }}>x{item.quantity} × CHF {item.price?.toFixed(2)}</p>
              </div>
              <strong style={{ color: 'var(--primary)' }}>CHF {(item.price * item.quantity).toFixed(2)}</strong>
            </div>
          ))}
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, color: 'var(--gray-600)' }}><span>Subtotal</span><span>CHF {order.subtotal?.toFixed(2)}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, color: 'var(--gray-600)' }}><span>Shipping</span><span>CHF {order.shippingCost?.toFixed(2)}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 18, fontWeight: 800, color: 'var(--primary)', paddingTop: 12, borderTop: '2px solid var(--primary)', marginTop: 4 }}><span>Total</span><span>CHF {order.total?.toFixed(2)}</span></div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ background: 'var(--white)', borderRadius: 'var(--radius-lg)', padding: 24, boxShadow: 'var(--shadow-sm)', border: '1px solid var(--gray-100)' }}>
            <h4 style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, color: 'var(--primary)' }}><MapPin size={16} /> Shipping Address</h4>
            <p style={{ fontSize: 14, color: 'var(--gray-700)', lineHeight: 1.8 }}>
              {order.shippingAddress?.firstName} {order.shippingAddress?.lastName}<br />
              {order.shippingAddress?.street}<br />
              {order.shippingAddress?.postalCode} {order.shippingAddress?.city}<br />
              {order.shippingAddress?.country}
            </p>
          </div>
          <div style={{ background: 'var(--white)', borderRadius: 'var(--radius-lg)', padding: 24, boxShadow: 'var(--shadow-sm)', border: '1px solid var(--gray-100)' }}>
            <h4 style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, color: 'var(--primary)' }}><CreditCard size={16} /> Payment</h4>
            <p style={{ fontSize: 14, color: 'var(--gray-600)', textTransform: 'capitalize' }}>{order.paymentMethod?.replace('_', ' ')}</p>
            <span style={{ display: 'inline-block', marginTop: 6, background: order.paymentStatus === 'paid' ? '#10b98122' : '#f59e0b22', color: order.paymentStatus === 'paid' ? '#10b981' : '#f59e0b', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700, textTransform: 'capitalize' }}>{order.paymentStatus}</span>
          </div>
          {order.trackingNumber && (
            <div style={{ background: 'var(--primary)', borderRadius: 'var(--radius-lg)', padding: 24 }}>
              <h4 style={{ color: 'var(--gold)', marginBottom: 8 }}>Tracking</h4>
              <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14 }}>Tracking No: <strong style={{ color: '#fff' }}>{order.trackingNumber}</strong></p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
